import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Slider,
  TextField,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  LocalFireDepartment as FireIcon,
  MyLocation as MyLocationIcon,
} from '@mui/icons-material';

import { useMobileLayout } from '../../utils/useMobileLayout';
import { fdiCategory, fdiTextOn } from '../../utils/fdiCategory';
import { getForecast } from '../../api/public';

// DAFF Lowveld Model — wind speed (km/h) → addend on initial FDI.
const getWindCorrection = (windSpeed) => {
  if (windSpeed <= 2) return 0;
  if (windSpeed <= 8) return 5;
  if (windSpeed <= 16) return 10;
  if (windSpeed <= 25) return 15;
  if (windSpeed <= 32) return 20;
  if (windSpeed <= 36) return 25;
  if (windSpeed <= 41) return 30;
  if (windSpeed <= 45) return 35;
  return 40;
};

// DAFF Lowveld Model — rainfall mm × days-since-rain → multiplier.
const getRainCorrection = (rainfall, days) => {
  if (rainfall === 0 || days === 0) return 1.0;
  const table = [
    [0.1, 2.6,    [0.7, 0.9, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0]],
    [2.7, 5.2,    [0.6, 0.8, 0.9, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0]],
    [5.3, 7.6,    [0.5, 0.7, 0.9, 0.9, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0]],
    [7.7, 10.2,   [0.4, 0.6, 0.8, 0.9, 0.9, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0]],
    [10.3, 12.8,  [0.4, 0.6, 0.7, 0.8, 0.9, 0.9, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0]],
    [12.9, 15.3,  [0.3, 0.5, 0.7, 0.8, 0.8, 0.9, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0]],
    [15.4, 20.5,  [0.2, 0.5, 0.6, 0.7, 0.8, 0.8, 0.9, 1.0, 1.0, 1.0, 1.0, 1.0]],
    [20.6, 25.5,  [0.2, 0.4, 0.5, 0.7, 0.7, 0.8, 0.9, 1.0, 1.0, 1.0, 1.0, 1.0]],
    [25.6, 38.4,  [0.1, 0.3, 0.4, 0.6, 0.6, 0.7, 0.8, 0.9, 1.0, 1.0, 1.0, 1.0]],
    [38.5, 51.1,  [0.1, 0.2, 0.4, 0.5, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0, 1.0, 1.0]],
    [51.2, 63.8,  [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.7, 0.8, 0.9, 1.0, 1.0]],
    [63.9, 76.5,  [0.1, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.8, 0.9, 1.0]],
    [76.6, Infinity, [0.1, 0.1, 0.1, 0.2, 0.4, 0.5, 0.6, 0.6, 0.7, 0.8, 0.9, 1.0]],
  ];
  let dayIndex;
  if (days <= 6) dayIndex = days - 1;
  else if (days <= 8) dayIndex = 6;
  else if (days <= 10) dayIndex = 7;
  else if (days <= 12) dayIndex = 8;
  else if (days <= 15) dayIndex = 9;
  else if (days <= 20) dayIndex = 10;
  else dayIndex = 11;

  for (const [minR, maxR, factors] of table) {
    if (rainfall >= minR && rainfall <= maxR) return factors[dayIndex];
  }
  return 1.0;
};

const calculateFDI = (temp, humidity, windSpeed = 0, rainfall = 0, daysSinceRain = 0) => {
  if (temp == null || humidity == null) return null;
  const initialFdi = (temp - 35) - ((35 - temp) / 30) + ((100 - humidity) * 0.37) + 30;
  const windAdj = initialFdi + getWindCorrection(windSpeed);
  const rainCorr = getRainCorrection(rainfall, daysSinceRain);
  return Math.round(windAdj * rainCorr);
};

// Find the hour entry closest to a given target hour-of-day in a forecast day.
const closestHour = (hourly, targetHour) =>
  hourly.reduce((prev, curr) => {
    const currDiff = Math.abs(new Date(curr.timestamp).getHours() - targetHour);
    const bestDiff = Math.abs(new Date(prev.timestamp).getHours() - targetHour);
    return currDiff < bestDiff ? curr : prev;
  });

// Manual FDI calculator extracted from ForecastPage. Renders fullScreen
// on mobile so the form has room to breathe; centered dialog on desktop.
//
// Props:
//   open, onClose       — dialog control
//   prefillFromForecast — optional forecast[selectedDay] passed in by the
//                         page so the "Time" input can populate other
//                         fields from the corresponding forecast hour.
const FdiCalculatorDialog = ({ open, onClose, prefillFromForecast }) => {
  const { isMobile } = useMobileLayout();

  const [calcTemp, setCalcTemp] = useState(30);
  const [calcHumidity, setCalcHumidity] = useState(40);
  const [calcWindSpeed, setCalcWindSpeed] = useState(10);
  const [calcRainfall, setCalcRainfall] = useState(0);
  const [calcDaysSinceRain, setCalcDaysSinceRain] = useState(0);
  const [calcTime, setCalcTime] = useState('14:00');
  const [calcLoading, setCalcLoading] = useState(false);

  const calcFdiResult = calculateFDI(calcTemp, calcHumidity, calcWindSpeed, calcRainfall, calcDaysSinceRain);
  const category = fdiCategory(calcFdiResult);
  const initialFdi = ((calcTemp - 35) - ((35 - calcTemp) / 30) + ((100 - calcHumidity) * 0.37) + 30).toFixed(1);
  const windCorr = getWindCorrection(calcWindSpeed);
  const rainCorr = getRainCorrection(calcRainfall, calcDaysSinceRain);

  const handleCalcTimeChange = (newTime) => {
    setCalcTime(newTime);
    if (prefillFromForecast?.hourly?.length) {
      const targetHour = parseInt(newTime.split(':')[0], 10);
      const closest = closestHour(prefillFromForecast.hourly, targetHour);
      if (closest) {
        setCalcTemp(Math.round(closest.temperature));
        setCalcHumidity(Math.round(closest.humidity));
        setCalcWindSpeed(Math.round(closest.wind_speed));
        setCalcRainfall(closest.precipitation || 0);
      }
    }
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) return;
    setCalcLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const data = await getForecast({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          const days = data.forecast;
          if (days && days.length > 0) {
            const now = new Date();
            const currentHour = now.getHours();
            setCalcTime(`${String(currentHour).padStart(2, '0')}:00`);
            const closest = closestHour(days[0].hourly, currentHour);
            setCalcTemp(Math.round(closest.temperature));
            setCalcHumidity(Math.round(closest.humidity));
            setCalcWindSpeed(Math.round(closest.wind_speed));
            setCalcRainfall(closest.precipitation || 0);
          }
        } catch (err) {
          console.error('Failed to fetch weather for current location:', err);
        } finally {
          setCalcLoading(false);
        }
      },
      () => setCalcLoading(false),
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen={isMobile} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Manual FDI Calculator
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Calculates the Fire Danger Index using the DAFF Lowveld Model (National Veld and Forest Fire Act, 1998).
        </Typography>

        <Grid container spacing={3}>
          {/* Inputs */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                label="Time"
                type="time"
                value={calcTime}
                onChange={(e) => handleCalcTimeChange(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                helperText={prefillFromForecast ? 'Change to populate from this day’s forecast at that hour.' : ' '}
              />
              <TextField
                label="Temperature (°C)"
                type="number"
                value={calcTemp}
                onChange={(e) => setCalcTemp(Number(e.target.value))}
                fullWidth
                size="small"
              />
              <Box>
                <Typography variant="body2" gutterBottom>Humidity: {calcHumidity}%</Typography>
                <Slider
                  value={calcHumidity}
                  onChange={(_, v) => setCalcHumidity(v)}
                  min={0}
                  max={100}
                  valueLabelDisplay="auto"
                />
              </Box>
              <TextField
                label="Wind Speed (km/h)"
                type="number"
                value={calcWindSpeed}
                onChange={(e) => setCalcWindSpeed(Number(e.target.value))}
                fullWidth
                size="small"
              />
              <TextField
                label="Recent Rainfall (mm)"
                type="number"
                value={calcRainfall}
                onChange={(e) => setCalcRainfall(Number(e.target.value))}
                fullWidth
                size="small"
              />
              <TextField
                label="Days Since Rain"
                type="number"
                value={calcDaysSinceRain}
                onChange={(e) => setCalcDaysSinceRain(Math.max(0, Number(e.target.value)))}
                fullWidth
                size="small"
              />
              <Button
                variant="outlined"
                startIcon={<MyLocationIcon />}
                onClick={handleUseCurrentLocation}
                disabled={calcLoading}
              >
                {calcLoading ? 'Fetching…' : 'Use current location + time'}
              </Button>
            </Box>
          </Grid>

          {/* Result + formula breakdown */}
          <Grid item xs={12} md={6}>
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                textAlign: 'center',
                borderColor: category.color,
                borderWidth: 2,
              }}
            >
              <FireIcon sx={{ fontSize: 48, color: category.color, mb: 1 }} />
              <Typography variant="h3" sx={{ color: category.color, fontWeight: 'bold' }}>
                {calcFdiResult ?? '—'}
              </Typography>
              <Chip
                label={category.level}
                sx={{
                  mt: 1,
                  mb: 1,
                  backgroundColor: category.color,
                  color: fdiTextOn(calcFdiResult),
                  fontWeight: 'bold',
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {category.advice}
              </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 }, mt: 2, backgroundColor: 'grey.50' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Formula Breakdown</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: { xs: '0.75rem', sm: '0.8rem' }, mb: 0.5 }}>
                1. Initial FDI = (T-35) - ((35-T)/30) + ((100-RH)*0.37) + 30
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: { xs: '0.75rem', sm: '0.8rem' }, mb: 0.5, pl: 2 }}>
                = ({calcTemp}-35) - ((35-{calcTemp})/30) + ((100-{calcHumidity})*0.37) + 30
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: { xs: '0.75rem', sm: '0.8rem' }, mb: 1, pl: 2, fontWeight: 'bold' }}>
                = {initialFdi}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: { xs: '0.75rem', sm: '0.8rem' }, mb: 0.5 }}>
                2. Wind correction ({calcWindSpeed} km/h): +{windCorr}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: { xs: '0.75rem', sm: '0.8rem' }, mb: 0.5, pl: 2, fontWeight: 'bold' }}>
                = {(parseFloat(initialFdi) + windCorr).toFixed(1)}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: { xs: '0.75rem', sm: '0.8rem' }, mb: 0.5 }}>
                3. Rain correction ({calcRainfall}mm, {calcDaysSinceRain} days): ×{rainCorr}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: { xs: '0.75rem', sm: '0.8rem' }, pl: 2, fontWeight: 'bold' }}>
                Final FDI = {calcFdiResult}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FdiCalculatorDialog;
